import {
  ColumnValidationResult,
  HomeOwnerImportModel,
  ImportResult,
  ImportStatus,
  RowValidationError,
  SystemReferenceData,
  ValidationStatus,
} from 'src/pages/Property/HomeOwnerImportDialogue/UseHomeownerImporter/types';
import { useCallback, useState } from 'react';
import {
  buildSystemReferenceData,
  isNonEmptyRow,
  mapRowToModel,
  validateHomeownerRows,
  validatePrimaryHomeownerConstraint,
} from 'src/pages/Property/HomeOwnerImportDialogue/UseHomeownerImporter/helpers';
import { toast } from 'react-toastify';
import { importContacts } from 'src/services/client';
import { ISuite } from 'src/components/SharedTypes';
import { parseCsvAndValidateColumns } from 'src/utils/CommonHelpers/sharedCSVUtils';

export interface UseHomeownerImportReturn {
  file: File | null;
  validationStatus: ValidationStatus;
  columnValidation: ColumnValidationResult;
  parsedRecords: HomeOwnerImportModel[];
  rowValidationErrors: RowValidationError[]; // ← new
  importStatus: ImportStatus;
  importResult: ImportResult | null;
  apiError: string | null;
  processFile: (file: File) => void;
  runImport: () => Promise<void>;
  reset: () => void;
}

/* The required column headers — must match the CSV template exactly.
 * Validation normalises both sides (trim + lowercase) so minor whitespace
 * differences in user files are forgiven, but casing must match the template.
 * If we ever add a column to the template, add it here too. */
export const REQUIRED_COLUMNS: string[] = [
  'First Name',
  'Last Name',
  'Email',
  'Phone',
  'Property',
  'Building',
  'Unit',
  'Primary',
  'Dashboard Only',
  'Designate',
  'Role'
];

export const useHomeownerImport = (
  filteredSuiteData: ISuite[]
): UseHomeownerImportReturn => {
  const [file, setFile] = useState<File | null>(null);
  const [validationStatus, setValidationStatus] =
    useState<ValidationStatus>('idle');
  const [columnValidation, setColumnValidation] =
    useState<ColumnValidationResult>({ found: [], missing: [] });
  const [parsedRecords, setParsedRecords] = useState<HomeOwnerImportModel[]>(
    []
  );
  const [rowValidationErrors, setRowValidationErrors] = useState<
    RowValidationError[]
  >([]);
  const [importStatus, setImportStatus] = useState<ImportStatus>('idle');
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);

  /* Called when the user drops or selects a CSV file.
   * Parses synchronously via PapaParse and validates the column headers.
   * No network call is made here. */
  const processFile = (selectedFile: File) => {
    setFile(selectedFile);
    setValidationStatus('validating');
    setApiError(null);

    const reader = new FileReader();

    reader.onload = (e: ProgressEvent<FileReader>) => {
      const text = e.target?.result as string;

      const { data, columnValidation, isValid } = parseCsvAndValidateColumns(
        text,
        REQUIRED_COLUMNS
      );

      setColumnValidation(columnValidation);

      if (!isValid) {
        setValidationStatus('invalid');
        setParsedRecords([]);
        setRowValidationErrors([]);
        return;
      }

      const nonEmptyRows: any[] = data.filter(isNonEmptyRow);
      const mapped: HomeOwnerImportModel[] = nonEmptyRows.map(mapRowToModel);

      const referenceData: SystemReferenceData =
        buildSystemReferenceData(filteredSuiteData);

      const referenceErrors: RowValidationError[] = validateHomeownerRows(
        mapped,
        referenceData
      );

      const primaryErrors: RowValidationError[] =
        validatePrimaryHomeownerConstraint(mapped);

      const errors: RowValidationError[] = [
        ...referenceErrors,
        ...primaryErrors,
      ];

      if (errors.length > 0) {
        setRowValidationErrors(errors);
        setParsedRecords(mapped);
        return setValidationStatus('invalid');
      }

      setParsedRecords(mapped);
      setRowValidationErrors([]);
      setValidationStatus('valid');
    };

    reader.onerror = () => {
      setValidationStatus('invalid');
      setColumnValidation({ found: [], missing: REQUIRED_COLUMNS });
    };

    reader.readAsText(selectedFile);
  };

  /**
   * POST the mapped records to api/units/homeowners.
   * Only callable once validation has passed (parsedRecords is populated).
   */
  const runImport = async (): Promise<void> => {
    if (!parsedRecords.length) {
      return;
    }

    try {
      setImportStatus('importing');
      setApiError(null);

      const response: any = await importContacts(parsedRecords);

      if (!response.ok) {
        let errorMessage: string = `Import failed (${response.status})`;

        const errorBody = await response.json();

        if (response.status === 422) {
          // Building/unit mismatch - use array and join for proper formatting.
          const messageParts = [
            "One or more units could not be found. This usually happens when the building name in your CSV doesn't match the building assigned to that unit in the system.",
            '',
            'Please verify that:',
            '• Unit numbers exist in the system',
            "• Building names exactly match what's configured in Juniper (case-sensitive)",
            '• There are no typos in unit or building names',
          ];

          // If the API provides specific details, append them
          if (errorBody?.message) {
            messageParts.push('', `Details: ${errorBody.message}`);
          }

          errorMessage = messageParts.join('\n');
        } else if (errorBody?.message) {
          errorMessage = errorBody.message;
        } else if (typeof errorBody === 'string') {
          errorMessage = errorBody;
        }

        setApiError(errorMessage);
        setImportStatus('error');

        return;
      }

      setImportResult({ totalRecords: parsedRecords.length });
      setImportStatus('success');

      toast.success(
        `${parsedRecords.length} user(s) imported successfully!`,
        { autoClose: 8000, position: 'top-center' }
      );
    } catch (error) {
      console.error('Homeowner import error:', error);

      const errorMessage = 'Something went wrong — please try again.';
      setApiError(errorMessage);
      setImportStatus('error');

      toast.error(`🚫 ${errorMessage}`, {
        autoClose: 12000,
        position: 'top-center',
      });
    }
  };

  const reset = useCallback((): void => {
    setFile(null);
    setValidationStatus('idle');
    setColumnValidation({ found: [], missing: [] });
    setParsedRecords([]);
    setImportStatus('idle');
    setImportResult(null);
    setApiError(null);
  }, []);

  return {
    file,
    validationStatus,
    columnValidation,
    parsedRecords,
    importStatus,
    importResult,
    apiError,
    rowValidationErrors,
    processFile,
    runImport,
    reset,
  };
};
